/**
 * socialProof — helpers for a warmer "online count" experience.
 *
 * WorldVibe is early. On real numbers alone a first-time visitor sees "3
 * travelers online" and bounces. To keep the vibe inviting we render a
 * *lively* count that grows with real activity, sways gently by the hour
 * of the day, and never drops below a friendly floor. Real users always
 * appear real — this only affects the *number* shown next to the
 * "travelers online" label. Clicking through still surfaces the real
 * online list.
 *
 * Numbers are deterministic-per-minute so the value doesn't jitter every
 * render; a subtle minute-level wobble keeps it feeling alive.
 */

function _now() {
  return new Date();
}

function _hourWave() {
  // A gentle sine-shaped ebb-and-flow across the 24h UTC day: peak in the
  // late evening (~20:00 UTC when EU + Americas overlap), trough at ~08:00
  // UTC (Asia asleep, EU commuting).
  const h = _now().getUTCHours() + _now().getUTCMinutes() / 60;
  return Math.sin(((h - 8) / 24) * Math.PI * 2); // -1..1
}

function _minuteJitter() {
  // ± ~10 seat wobble that changes each minute so the counter breathes.
  const m = _now().getUTCMinutes() + _now().getUTCSeconds() / 60;
  return Math.sin((m / 60) * Math.PI * 2);
}

function _hash32(str) {
  // Tiny deterministic string hash (Fowler-Noll-Vo variant). Good enough
  // for "give each planet a stable warm base number".
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Global online count for the home greeting, friends card, etc.
 *
 * With ~0 real users we sit at the floor. With 100 real users we bloom to
 * ~1100. Peaks around evening, dips overnight, wobbles by the minute.
 *
 * @param {number} realCount  How many users are actually online right now.
 * @param {object} [opts]
 * @param {number} [opts.floor]   Minimum value even at the quietest hour.
 * @param {number} [opts.perReal] Extra "friends of friends" per real user.
 */
export function attractiveOnlineCount(realCount, opts = {}) {
  const floor = opts.floor ?? 500;
  const perReal = opts.perReal ?? 6;
  const hourAmp = opts.hourAmp ?? 90;
  const jitterAmp = opts.jitterAmp ?? 12;
  const real = Math.max(0, Number(realCount) || 0);
  const value = floor + real * perReal + hourAmp * _hourWave() + jitterAmp * _minuteJitter();
  return Math.max(floor, Math.round(value));
}

/**
 * Per-planet online count. Each planet gets a stable "warm base" derived
 * from its id so smaller planets don't all show the same number. Values
 * scale with real users but always feel populated.
 *
 * Deterministic per (planetId, realCount) within the same hour so the
 * number shown on Home matches the number inside the Planet — no more
 * "63 → 71" jump when the user taps through.
 *
 * @param {number} realCount  Real users active in this planet.
 * @param {string} planetId   Stable id used to seed the base.
 */
export function attractivePlanetCount(realCount, planetId = '') {
  const real = Math.max(0, Number(realCount) || 0);
  // Stable per-planet base between 24 and 108 travelers.
  const base = 24 + (_hash32(String(planetId)) % 84);
  // Hour-of-day breathing: ±22% around the base. Changes slowly — safe
  // for cross-page consistency because both views usually render within
  // seconds of each other.
  const wave = 1 + 0.22 * _hourWave();
  // Per-planet-seeded jitter (NOT time-based) so the same planet always
  // shows the same offset from Home to PlanetDetail. Range roughly ±7.
  const seededJitter = ((_hash32(String(planetId) + ':jitter') % 1400) / 100) - 7;
  const value = base * wave + real * 4 + seededJitter;
  return Math.max(base, Math.round(value));
}
